import * as React from 'react';
import {
	DataProcessingDefinition,
	AssetGroup,
	Asset,
	Pageable,
	fetchAssetsByAssetGroupId
} from '@deviceinsight/ng-ui-api-client';

import {Modal, Button} from '@deviceinsight/ng-ui-basic-components';

import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import ImpactContainer from './ImpactContainer';
import AssetGroupTable from './AssetGroupTable';
import UnShareableInformation from './UnShareableInformation';
import {AssetsPerAssetGroup, DataProcessingDefinitionValidationErrors} from './types';

type Props = {
	dataProcessingDefinition: DataProcessingDefinition;
	assetGroupIds: Array<number>;
	dpdValidationErrors: DataProcessingDefinitionValidationErrors;
	hide: () => void;
	apply: (assetGroup: AssetGroup, dataProcessingDefinitionId: number) => void;
};

type State = {
	loading: boolean;
	assetsPerAssetGroup: AssetsPerAssetGroup | null | undefined;
	selectedAssetGroup: AssetGroup | null | undefined;
};

const getDialogHeader = (kpiName: string) => (
	<>
		<Trans i18nKey="assets.detail.dataProcessingDefinition.shareModalTitle">Share Data Processing Definition</Trans>
		<div
			style={{
				fontSize: '12px'
			}}>
			{kpiName}
		</div>
	</>
);

export default class ShareDialog extends React.Component<Props, State> {
	state: State = {
		loading: true,
		assetsPerAssetGroup: undefined,
		selectedAssetGroup: undefined
	};

	componentDidMount() {
		const {assetGroupIds} = this.props;
		const assetsForAssetGroups = assetGroupIds.map((id) => fetchAssetsByAssetGroupId(id));
		Promise.all(assetsForAssetGroups).then((assets: Array<Pageable<Asset>>) => {
			const initialValue: AssetsPerAssetGroup = {};
			const assetsPerAssetGroup = assets.reduce((result, assignedAssets, i) => {
				const assetGroupId = assetGroupIds[i];
				result[assetGroupId] = assignedAssets.content;
				return result;
			}, initialValue);

			this.setState({
				assetsPerAssetGroup,
				loading: false
			});
		});
	}

	onSelectAssetGroup: (selectedAssetGroup: AssetGroup) => void = (selectedAssetGroup: AssetGroup) =>
		this.setState({
			selectedAssetGroup
		});
	onApplyShare: () => void = () => {
		const {
			dataProcessingDefinition: {id},
			apply
		} = this.props;
		const {selectedAssetGroup} = this.state;

		if (selectedAssetGroup) {
			apply(selectedAssetGroup, id);
		}
	};
	onRejectShare: () => void = () => {
		this.setState({
			selectedAssetGroup: undefined
		});
	};

	render() {
		const {
			dataProcessingDefinition,
			dataProcessingDefinition: {
				definition: {kpiName}
			},
			dpdValidationErrors,
			assetGroupIds,
			hide
		} = this.props;
		const {selectedAssetGroup, assetsPerAssetGroup} = this.state;
		const isSharable = !!selectedAssetGroup && !dpdValidationErrors[selectedAssetGroup.id];
		const currentDpdValidationErrors = selectedAssetGroup ? dpdValidationErrors[selectedAssetGroup.id] : [];
		return (
			<Modal
				header={getDialogHeader(kpiName)}
				content={
					selectedAssetGroup ? (
						isSharable ? (
							<ImpactContainer
								assetGroup={selectedAssetGroup}
								dataProcessingDefinition={dataProcessingDefinition}
							/>
						) : (
							<UnShareableInformation
								selectedAssetGroup={selectedAssetGroup}
								kpiName={kpiName}
								currentDpdValidationErrors={currentDpdValidationErrors}
							/>
						)
					) : (
						<AssetGroupTable
							assetGroupIds={assetGroupIds}
							assetsPerAssetGroup={assetsPerAssetGroup}
							onSelect={this.onSelectAssetGroup}
						/>
					)
				}
				footer={
					selectedAssetGroup && (
						<>
							{isSharable && (
								<Button primary onClick={this.onApplyShare}>
									<Trans i18nKey="assets.detail.dataProcessingDefinition.sharedialog.sharebutton">
										Share
									</Trans>
								</Button>
							)}
							<Button onClick={this.onRejectShare}>
								<Trans i18nKey="assets.detail.dataProcessingDefinition.sharedialog.cancel">
									Cancel
								</Trans>
							</Button>
						</>
					)
				}
				onOutsideClick={hide}
			/>
		);
	}
}
